import {
  AlertComponent,
  AttachedDesignUploadNotice,
  FormSection,
  ModalComponent,
  NavBar,
  PdfPullingReport,
  PullingAdditionalInfo,
  PullingDetailsTable,
  PullingForm,
  ShowContent,
  Spinner,
  UploadFileForm,
} from "../../components";
import styles from "../main.module.sass";
import { useContext, useEffect, useState } from "react";
import DataContext from "../../context/DataContext";
import {
  useAttachedDesignUpload,
  useUploadedDocumentDownload,
} from "../../hooks";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { PDFView } from "../../components/pdf";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import DesignerContext from "../../context/DesignerContext";
import { pulling_initial_state } from "../../utils/data";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ListAltIcon from "@mui/icons-material/ListAlt";
import BuildIcon from "@mui/icons-material/Build";
import DriveFolderUploadIcon from "@mui/icons-material/DriveFolderUpload";
import { DocumentProps } from "../../interfaces/interfaces";

export const PullingDesignForm = () => {
  const navigate = useNavigate();
  let [searchParams] = useSearchParams();
  const pdf = searchParams.get("pdf");
  const [inEdit, setInEdit] = useState(false);
  const [inPdf, setInPdf] = useState(pdf || false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [isUploadSuccess, setIsUploadSuccess] = useState(false);
  const [isUploadError, setIsUploadError] = useState(false);
  const { idPulling } = useParams();
  const { fetchDataClient, fetchDataProducts } = useContext(DataContext);
  const [isReadyToSave, setIsReadyToSave] = useState(false);
  const {
    isSuccess,
    isError,
    isLoading,
    pullingInfo,
    onEditPullingInfo,
    fetchDataPulling,
    handleCreatePulling,
    handleEditPulling,
  } = useContext(DesignerContext);
  const savedPullingId =
    idPulling && idPulling !== "0" ? parseInt(idPulling) : null;
  const { attachedUpload, refreshAttachedUpload } = useAttachedDesignUpload({
    endpointBase: "pulling-list",
    designId: savedPullingId,
    designIdField: "pullingDesignId",
    wellId: pullingInfo.well?.id,
    enabled: Boolean(savedPullingId),
  });
  const { downloadUploadedDocument, isDownloadingUploadedDocument } =
    useUploadedDocumentDownload("pdf-pulling-adm");

  useEffect(() => {
    fetchDataClient();
    if (idPulling && idPulling !== "0") {
      fetchDataPulling(parseInt(idPulling));
      setInEdit(true);
    } else {
      onEditPullingInfo(pulling_initial_state);
    }
  }, [idPulling]);

  useEffect(() => {
    if (pullingInfo.well.id !== 0) {
      setIsReadyToSave(true);
    } else {
      setIsReadyToSave(false);
    }
  }, [pullingInfo]);

  useEffect(() => {
    fetchDataProducts();
  }, []);

  useEffect(() => {
    setTimeout(() => {
      setIsUploadSuccess(false);
      setIsUploadError(false);
    }, 4000);
  }, [isUploadSuccess, isUploadError]);

  const handleUploadPulling = async ({
    client,
    well,
    customName,
    file,
    installationDate,
    pullingDesignId,
  }: DocumentProps) => {
    try {
      setIsUploadingDocument(true);
      setIsUploadModalOpen(false);
      const formData = new FormData();
      formData.append("client", JSON.stringify(client));
      formData.append("well", JSON.stringify(well));
      formData.append(
        "customName",
        customName || pullingInfo.customName || pullingInfo.pullingName || "",
      );
      installationDate &&
        formData.append("installationDate", installationDate.toISOString());
      pullingDesignId &&
        formData.append("pullingDesignId", String(pullingDesignId));
      formData.append("type", "pulling");
      formData.append("file", file, file.name);
      const response = await fetch(
        process.env.REACT_APP_DEV_API + "/pulling-upload",
        { method: "POST", body: formData },
      );
      const res = await response.json();
      if (res.success) {
        setIsUploadSuccess(true);
        refreshAttachedUpload();
      } else setIsUploadError(true);
    } catch {
      setIsUploadError(true);
    } finally {
      setIsUploadingDocument(false);
    }
  };

  const buttons = [
    {
      title: "Cancel",
      action: () => {
        onEditPullingInfo(pulling_initial_state);
        navigate(-1);
      },
      icon: <CancelIcon />,
      disabled: !!inPdf,
    },
    {
      title: "Save",
      action: inEdit
        ? () => idPulling && handleEditPulling(idPulling)
        : handleCreatePulling,
      icon: <SaveIcon />,
      disabled: !isReadyToSave,
    },
    {
      title: "Upload",
      action: () => setIsUploadModalOpen(true),
      icon: <DriveFolderUploadIcon />,
      disabled: !pullingInfo.well?.id || !pullingInfo.well?.client?.id,
    },
    {
      title: !inPdf ? "To Pdf" : "Go Back",
      action: () => {
        setInPdf(!inPdf);
      },
      icon: !inPdf ? <PictureAsPdfIcon /> : <ArrowBackIosIcon />,
    },
  ];

  let content = (
    <>
      <NavBar title="Pulling Report Design" buttons={buttons} />
      <div className={styles.techProposalForm}>
        {!inPdf && (
          <AttachedDesignUploadNotice
            designLabel="pulling design"
            isDownloading={isDownloadingUploadedDocument}
            upload={attachedUpload}
            onDownload={() =>
              attachedUpload &&
              downloadUploadedDocument(
                attachedUpload.path,
                attachedUpload.customName,
              )
            }
            onReplace={() => setIsUploadModalOpen(true)}
          />
        )}
        {inPdf ? (
          <PDFView
            fileName={
              pullingInfo.pullingName +
              "-" +
              pullingInfo.well.client?.name +
              "-" +
              pullingInfo.well.name +
              "-"
            }
            document={() => <PdfPullingReport pullingInfo={pullingInfo} />}
          />
        ) : (
          <>
            <FormSection title="Pulling Design" icon={<InfoOutlinedIcon />}>
              <PullingForm />
            </FormSection>

            <FormSection title="Details" icon={<ListAltIcon />}>
              <PullingDetailsTable />
            </FormSection>

            <FormSection title="Additional Info" icon={<BuildIcon />}>
              <PullingAdditionalInfo />
            </FormSection>
          </>
        )}
        {isSuccess && <AlertComponent type="success" />}
        {isError && <AlertComponent type="error" />}
        {isUploadSuccess && <AlertComponent type="success" />}
        {isUploadError && <AlertComponent type="error" />}
      </div>
      {isLoading && <Spinner message={inEdit ? "Saving..." : "Creating..."} />}
      {isUploadingDocument && <div className={styles.backdrop}></div>}
      {isUploadModalOpen && (
        <ModalComponent
          modalContent={
            <UploadFileForm
              clientOptions={
                pullingInfo.well.client ? [pullingInfo.well.client] : []
              }
              wellOptions={[pullingInfo.well]}
              type="pulling"
              fetchDataWells={() => {}}
              initialValues={{
                client: pullingInfo.well.client || { id: 0, name: "" },
                well: pullingInfo.well,
                customName:
                  pullingInfo.customName || pullingInfo.pullingName || "",
                pullingDesignId: savedPullingId,
              }}
              lockClientWell
              onUploadFile={(values: DocumentProps) =>
                handleUploadPulling(values)
              }
              onCancel={() => setIsUploadModalOpen(false)}
            />
          }
        />
      )}
    </>
  );

  return <ShowContent error={""} isLoading={false} content={content} />;
};
